<template>
  <div class="rc" @mouseenter="hovered = true" @mouseleave="hovered = false">
    <!-- Image -->
    <div class="rc__img-wrap" @click="openDetails">
      <v-img
        :src="previewSrc"
        cover
        height="100%"
        @error="onPreviewError"
      >
        <template #placeholder>
          <div class="rc__img-placeholder">
            <v-progress-circular indeterminate color="white" size="24" />
          </div>
        </template>
      </v-img>

      <!-- Overlay hover -->
      <div class="rc__overlay" :class="{ 'rc__overlay--visible': hovered }">
        <button class="rc__overlay-btn" @click.stop="openDetails">
          <v-icon size="14">mdi-eye</v-icon>
          Voir
        </button>
      </div>

      <!-- Étoile favori -->
      <button
        class="rc__fav"
        :class="{ 'rc__fav--active': item.favorite }"
        @click.stop="toggleFavorite"
        :aria-label="item.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'"
      >
        <v-icon size="16">{{ item.favorite ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
      </button>
    </div>

    <!-- Corps -->
    <div class="rc__body">
      <p class="rc__title">{{ item.title || 'Sans titre' }}</p>
      <div class="rc__meta">
        <span v-if="item.totals?.kcal" class="rc__meta-item">
          <v-icon size="12">mdi-fire</v-icon>
          {{ Math.round(item.totals.kcal) }} kcal
        </span>
        <span v-if="item.servings" class="rc__meta-item">
          <v-icon size="12">mdi-silverware-fork-knife</v-icon>
          {{ item.servings }} portions
        </span>
      </div>
      <div v-if="item.category" class="rc__tags">
        <span class="rc__tag">{{ $t(item.category) }}</span>
      </div>
    </div>

    <!-- Bouton liste de courses -->
    <div class="rc__footer">
      <button class="rc__action-btn" @click="createShoppingList">
        <v-icon size="14">mdi-cart-plus</v-icon>
        Liste
      </button>
      <button
        class="rc__action-btn rc__action-btn--delete"
        @click="deleteRecipe"
        aria-label="Supprimer la recette"
      >
        <v-icon size="14">mdi-delete-outline</v-icon>
        Supprimer
      </button>
    </div>

    <v-snackbar v-model="snack.show" :timeout="2500">{{ snack.text }}</v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth_store";
import { addShoppingList, getUserShoppingLists } from "@/service/shopping_list_service";
import { getIngredientsByBarcodes } from "@/service/ingredient_service";
import { getUserRecipeById } from "@/service/recipe_service";
import { getBaseRecipeById } from "@/service/base_recipe_service";

const props = defineProps({
  item: { type: Object, required: true },
});

const emit = defineEmits(["toggle-favorite", "delete"]);
const router = useRouter();
const authStore = useAuthStore();
const hovered = ref(false);
const snack = ref({ show: false, text: "" });
const validPreview = ref(true);
const placeholderImage = 'https://www.mimisrecipes.com/wp-content/uploads/2018/12/recipe-placeholder-featured.jpg';

const sourceKey = computed(() => props.item?.url || props.item?.image || props.item?.imageUrl || '');
const previewSrc = computed(() => validPreview.value && sourceKey.value ? sourceKey.value : placeholderImage);

function onPreviewError() { validPreview.value = false; }
watch(sourceKey, (v) => { validPreview.value = !!v; });

function toggleFavorite() { emit("toggle-favorite", props.item); }
function openDetails() {
  router.push({ name: "recipe_detail", params: { id: props.item.id } });
}
function deleteRecipe() { emit("delete", props.item.id); }

async function createShoppingList() {
  if (!authStore.userid) {
    snack.value = { show: true, text: "Utilisateur non connecté." };
    return;
  }
  try {
    let r = await getUserRecipeById(authStore.userid, props.item.id);
    if (!r || r.baseRecipe === true) {
      const base = await getBaseRecipeById(props.item.id);
      if (base) r = base;
    }
    const ingredients = r?.ingredients || [];
    if (!ingredients.length) {
      snack.value = { show: true, text: "Aucun ingrédient à ajouter." };
      return;
    }
    const barcodes = ingredients.map((ing) => ing?.id).filter(Boolean);
    const fullInfos = barcodes.length ? await getIngredientsByBarcodes(authStore.userid, barcodes) : [];
    const items = ingredients.map((ing) => {
      const found = fullInfos.find((f) => f.id === ing?.id);
      const name = String(ing?.name || found?.productName || ing?.productName || ing?.id || "").trim();
      if (!name) return null;
      const grams = Number(ing.grams);
      const quantity = Number.isFinite(grams) && grams > 0 ? `${grams} g` : 1;
      return { name, quantity };
    }).filter(Boolean);
    if (!items.length) {
      snack.value = { show: true, text: "Aucun ingrédient valide." };
      return;
    }
    const listName = String(r?.title || props.item.title || "Liste de courses").trim();
    const existingLists = await getUserShoppingLists(authStore.userid);
    const alreadyGenerated = Object.values(existingLists || {}).some((list) => {
      if (!list) return false;
      return String(list.name || "").trim().toLowerCase() === listName.toLowerCase() && list.userGenerated === false;
    });
    if (alreadyGenerated) {
      snack.value = { show: true, text: "Cette liste existe déjà." };
      return;
    }
    await addShoppingList(authStore.userid, { name: listName, items, userGenerated: false });
    snack.value = { show: true, text: "Liste de courses créée." };
  } catch (e) {
    console.error(e);
    snack.value = { show: true, text: "Erreur lors de la création de la liste." };
  }
}
</script>

<style scoped>
.rc {
  display: flex;
  flex-direction: column;
  cursor: default;
}

/* Image */
.rc__img-wrap {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  background: rgba(var(--v-theme-text), 0.05);
  cursor: pointer;
}

.rc__img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Overlay hover */
.rc__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  pointer-events: none;
}
.rc__overlay--visible {
  background: rgba(0, 0, 0, 0.28);
  pointer-events: auto;
}

.rc__overlay-btn {
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.18s ease, transform 0.18s ease;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: #1A1615;
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
}
.rc__overlay--visible .rc__overlay-btn {
  opacity: 1;
  transform: translateY(0);
}

/* Favori */
.rc__fav {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(26, 22, 21, 0.5);
  transition: color 0.15s, background 0.15s;
  z-index: 2;
}
.rc__fav--active {
  color: #f59e0b;
}
.rc__fav:hover {
  background: #fff;
}

/* Corps */
.rc__body {
  padding: 10px 2px 6px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.rc__title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--v-theme-text));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.rc__meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rc__meta-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: rgba(var(--v-theme-text), 0.5);
}

.rc__tags {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.rc__tag {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06);
  color: rgba(var(--v-theme-text), 0.55);
  border: 0.5px solid rgba(var(--v-theme-text), 0.1);
}

/* Footer actions */
.rc__footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 2px 4px;
  margin-top: 2px;
}

.rc__action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 400;
  color: rgba(var(--v-theme-text), 0.5);
  background: none;
  border: 0.5px solid rgba(var(--v-theme-text), 0.15);
  border-radius: 999px;
  padding: 4px 10px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
}
.rc__action-btn:hover {
  background: rgba(var(--v-theme-text), 0.05);
  color: rgb(var(--v-theme-text));
}

.rc__action-btn--delete {
  color: rgba(180, 40, 40, 0.6);
  border-color: rgba(180, 40, 40, 0.2);
}
.rc__action-btn--delete:hover {
  background: rgba(180, 40, 40, 0.06);
  color: rgb(180, 40, 40);
}
</style>
